import { isBoolean } from '@mindtickle/utils';

import logger from '../../../config/app.logger';
import { getExtendedLogger } from '../../Logger';

import { LOCALE_DATE_FORMATS } from './localeDateFormats';
import {
  DEFAULT_LOCALE,
  DEFAULT_TIME_KEY,
  AVAILABLE_FORMATS,
  IS_24_HOUR_FORMAT_KEY,
  TIME_FORMAT_VARIANT,
  HOUR_FORMAT_24H,
  HOUR_FORMAT_12H,
  AVAILABLE_TIME_FORMAT_KEYS,
  LOGGER_NAME,
} from './constants';
import type {
  MutableLocaleDateConfig,
  UserLocaleDateConfig,
  LocaleBaseConfig,
  TimeFormatKey,
} from '../types';

const log = getExtendedLogger(LOGGER_NAME, logger);

export const resolveTimeVariant = (key: keyof TimeFormatKey, is24Hour: boolean): string => {
  const resolvedKey = AVAILABLE_TIME_FORMAT_KEYS.has(key) ? key : DEFAULT_TIME_KEY;
  return TIME_FORMAT_VARIANT[resolvedKey][is24Hour ? HOUR_FORMAT_24H : HOUR_FORMAT_12H];
};

const resolveLocale = (raw: LocaleBaseConfig): UserLocaleDateConfig => {
  const resolve = (key: keyof TimeFormatKey) => resolveTimeVariant(key, raw.is24HourFormat);
  return {
    ...raw,
    TIME_FORMAT: resolve(raw.TIME_FORMAT),
    GMT_OFF_SET: resolve(raw.GMT_OFF_SET),
    TIME_FORMAT_12H: resolve(raw.TIME_FORMAT_12H),
    TIME_FORMAT_24H: resolve(raw.TIME_FORMAT_24H),
    TIME_FORMAT_12H_WITH_SECONDS: resolve(raw.TIME_FORMAT_12H_WITH_SECONDS),
    TIME_FORMAT_24H_WITH_SECONDS: resolve(raw.TIME_FORMAT_24H_WITH_SECONDS),
  };
};

export const DateFormatConfig: UserLocaleDateConfig = resolveLocale(
  LOCALE_DATE_FORMATS(false)[DEFAULT_LOCALE]
);
log('DateFormatConfig initialised %o', DateFormatConfig);

export const resolveLocaleFormats = (
  locale: string,
  is24HourFormat?: boolean,
  overrides?: Partial<UserLocaleDateConfig>
): UserLocaleDateConfig => {
  const formats = LOCALE_DATE_FORMATS(is24HourFormat);
  const localeConfig = locale in formats ? formats[locale] : formats[DEFAULT_LOCALE];
  const resolvedLocaleConfig = resolveLocale(localeConfig);

  const result = (Object.keys(resolvedLocaleConfig) as Array<keyof UserLocaleDateConfig>).reduce(
    (acc, key) => {
      const overrideValue = overrides?.[key];

      if (
        (key === IS_24_HOUR_FORMAT_KEY && isBoolean(overrideValue)) ||
        AVAILABLE_FORMATS.has(overrideValue as string)
      ) {
        acc[key] = overrideValue;
      } else {
        acc[key] = resolvedLocaleConfig[key];
      }

      return acc;
    },
    {} as MutableLocaleDateConfig
  ) as UserLocaleDateConfig;

  log(
    'resolveLocaleFormats locale: %s, is24HourFormat: %s → TIME_FORMAT: %s',
    locale,
    is24HourFormat,
    result.TIME_FORMAT
  );
  return result;
};
